/**
 * Interim (mid-term) letter requests of the last four months — the page's second view, for people
 * who are not leaving and so are not on the leaver list. Each row carries the letter's ONE status
 * (`letterStatus`), worded as on the leaver list and the draft header.
 */
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Loader2 } from 'lucide-react';
import { fetchReferenceLetterRequests, markLetterDone } from '../../services/referenceLetterApi';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { ReferenceLetterRequest } from '../../types/referenceLetterDraft';
import { formatDate, markedDoneText } from './format';
import { DoneMenu } from './DoneMenu';
import { letterStatus, showsStatus, type StatusSelection } from './letterStatus';
import { LetterStatusBadge } from './LetterStatusBadge';
import { StatusFilter } from './StatusFilter';

/** An interim letter's one status; marked Done by hand wins. */
const statusOf = (r: ReferenceLetterRequest) => letterStatus({ ...r, done: Boolean(r.done) });

/** The interim request table with its filter; a row click hands the request to the page. */
export function RequestList({ onSelect, refreshKey = 0 }: { onSelect: (request: ReferenceLetterRequest) => void; refreshKey?: number }) {
  const [requests, setRequests] = useState<ReferenceLetterRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusSelection>(null);

  // A new `refreshKey` reloads it (a letter was sent, cancelled or marked meanwhile); the rows on
  // screen stay while it does, and a late answer from an older load is dropped.
  useEffect(() => {
    let current = true;
    fetchReferenceLetterRequests({ type: 'interim' })
      .then((r) => {
        if (!current) return;
        setRequests(r);
        setError(null);
      })
      .catch((err: Error) => current && setError(err.message));
    return () => {
      current = false;
    };
  }, [refreshKey]);

  // An older proxy sends no booleans: those rows have no status and no chips.
  const hasStatus = Boolean(requests?.some((r) => r.tasksSubmitted !== undefined));
  const searched = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!requests || !q) return requests;
    return requests.filter((r) => `${r.employeeName} ${r.supervisorName} ${r.template}`.toLowerCase().includes(q));
  }, [requests, filter]);
  const statuses = useMemo(() => (searched ?? []).map(statusOf), [searched]);
  /** Marks an interim letter Done by hand or takes it back, and shows the answer on the row. */
  const toggleDone = (r: ReferenceLetterRequest) => async (done: boolean) => {
    const mark = await markLetterDone({ recordId: r.id }, done);
    void logAnalyticsEvent('reference_letters_marked_done', { surface: 'interim', done });
    setRequests((list) => list?.map((x) => (x.id === r.id ? { ...x, done: mark } : x)) ?? list);
  };
  const shown = useMemo(
    () => searched?.filter((_, i) => showsStatus(statusFilter, statuses[i].label)) ?? null,
    [searched, statuses, statusFilter],
  );

  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-zinc-600">Interim letters requested in the last four months</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {hasStatus ? <StatusFilter statuses={statuses} selected={statusFilter} onChange={setStatusFilter} /> : <span />}
        <div className="w-64">
          <Input className="rounded" label="Filter by name or template" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>

      {error && <Banner variant="alert" description={error} />}
      {!requests && !error && (
        <p className="flex items-center gap-2 text-sm text-zinc-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading requests…
        </p>
      )}
      {shown && (
        <div className="overflow-x-auto rounded border border-solid border-pearl bg-white">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-zinc-600 [&_th]:font-normal">
              <tr>
                <th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="hidden px-3 py-2 md:table-cell">Template</th>
                <th className="px-3 py-2">Requested</th>
                <th className="px-3 py-2">Status</th>
                <th className="w-12 px-3 py-2"><span className="sr-only">More</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-zinc-600">No interim requests match.</td>
                </tr>
              )}
              {shown.map((r) => (
                <tr key={r.id} className="cursor-pointer border-t border-solid border-pearl hover:bg-zinc-50" onClick={() => onSelect(r)}>
                  <td className="px-3 py-2 text-zinc-900">
                    <Button type="button" variant="action" size="md" className="text-left before:hidden after:hidden" onClick={(e: MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); onSelect(r); }}>
                      {r.employeeName || 'Unnamed'}
                    </Button>
                  </td>
                  <td className="px-3 py-2 text-zinc-900">{r.supervisorName || '–'}</td>
                  <td className="hidden px-3 py-2 text-zinc-900 md:table-cell">{r.template || '–'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-zinc-900">{formatDate(r.created)}</td>
                  {/* An older proxy sends no booleans: say nothing rather than "missing". */}
                  <td className="px-3 py-2" title={r.done ? markedDoneText(r.done) : undefined}>
                    {r.tasksSubmitted !== undefined && <LetterStatusBadge status={statusOf(r)} />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DoneMenu done={Boolean(r.done)} onToggle={toggleDone(r)} label={r.employeeName || 'this letter'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
